import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty, Avatar, Statistic, Switch,
} from 'antd';
import {
  CrownOutlined, ReloadOutlined, EnvironmentOutlined, ClockCircleOutlined,
  RightOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getEvents, getVipWatch } from '../api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';
const GOLD    = '#fbbf24';

const fmtMins = (m) => {
  if (!m || m < 1) return '0m';
  if (m < 60) return `${Math.round(m)}m`;
  return `${Math.floor(m/60)}h ${Math.round(m%60)}m`;
};

export default function VipWatch() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [vips, setVips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    getVipWatch({ event_id: eventId })
      .then(r => setVips(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    load();
    if (autoRefresh && eventId) intervalRef.current = setInterval(load, 5000);
    return () => clearInterval(intervalRef.current);
  }, [load, autoRefresh, eventId]);

  const present = vips.filter(v => v.current_location).length;
  const offline = vips.length - present;

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <CrownOutlined style={{ marginRight: 8, color: GOLD }} />VIP Watch
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Real-time tracking of VIPs, Speakers and Special Guests · auto-refreshes every 5s
          </Text>
        </Col>
        <Col>
          <Space>
            <Space size={6}><Text style={{ fontSize: 12 }}>Auto-refresh</Text>
              <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" /></Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${GOLD}` }}>
            <Statistic title="Total VIPs" value={vips.length} prefix={<CrownOutlined />} valueStyle={{ color: GOLD }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #10b981' }}>
            <Statistic title="Currently Present" value={present} valueStyle={{ color: '#10b981' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #94a3b8' }}>
            <Statistic title="Not on Premises" value={offline} valueStyle={{ color: '#94a3b8' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Live Updates"
              value={autoRefresh ? 'ON' : 'OFF'}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: autoRefresh ? TEAL : '#94a3b8' }} />
          </Card>
        </Col>
      </Row>

      {vips.length === 0 ? (
        <Empty description="No VIP/Speaker/Guest categories found. Create them in Category Master." />
      ) : (
        <Row gutter={[16, 16]}>
          {vips.map(v => {
            const isPresent = !!v.current_location;
            const recentlySeen = v.minutes_since_seen != null && v.minutes_since_seen <= 5;
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={v.id}>
                <Card
                  hoverable
                  onClick={() => navigate(`/delegate-journey/${v.id}?event_id=${eventId}`)}
                  style={{
                    borderRadius: 12,
                    border: `2px solid ${isPresent ? '#10b981' : '#e5e7eb'}`,
                    boxShadow: isPresent ? `0 0 16px rgba(16, 185, 129, 0.18)` : 'none',
                    height: '100%',
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <div style={{ position: 'relative' }}>
                    {/* Live ping for currently present VIPs */}
                    {recentlySeen && (
                      <span style={{
                        position: 'absolute', top: -4, right: -4,
                        width: 12, height: 12, borderRadius: '50%',
                        background: '#10b981',
                        boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.8)',
                        animation: 'vipPing 1.6s ease-out infinite',
                      }}/>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <Avatar size={48} style={{
                        background: `linear-gradient(135deg, ${v.category_color || GOLD}, ${PRIMARY})`,
                        fontWeight: 700, fontSize: 18,
                      }}>
                        {(v.delegate_name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 14, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {v.delegate_name}
                        </Text>
                        {v.category && <Tag color={v.category_color} style={{ fontSize: 10, marginTop: 2 }}>{v.category}</Tag>}
                      </div>
                      <RightOutlined style={{ color: '#bbb', fontSize: 12 }} />
                    </div>

                    {v.company && (
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>🏢 {v.company}</Text>
                    )}

                    <div style={{
                      padding: '8px 10px', borderRadius: 8,
                      background: isPresent ? '#dcfce7' : '#f1f5f9',
                      marginBottom: 8,
                    }}>
                      <Space size={4}>
                        <EnvironmentOutlined style={{ color: isPresent ? '#10b981' : '#94a3b8' }} />
                        <Text strong style={{ color: isPresent ? '#166534' : '#64748b', fontSize: 12 }}>
                          {v.current_location || 'Not on premises'}
                        </Text>
                      </Space>
                    </div>

                    <Space wrap size={4} style={{ fontSize: 11 }}>
                      <Tag color="blue">{v.zones_visited || 0} zones</Tag>
                      <Tag color="cyan">{v.total_scans || 0} scans</Tag>
                      <Tag color="purple">{fmtMins(v.time_at_event)}</Tag>
                    </Space>

                    {v.last_seen && (
                      <div style={{ marginTop: 8, fontSize: 10, color: '#94a3b8' }}>
                        <ClockCircleOutlined /> Last seen {dayjs(v.last_seen).fromNow()}
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <style>{`
        @keyframes vipPing {
          0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.8); }
          70%  { box-shadow: 0 0 0 10px rgba(16,185,129,0); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
        }
      `}</style>
    </div>
  );
}
